import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LeaderboardEntry, PagewiseEntries } from '@sparro/app/core/models/leaderboard.model';
import { ImageOptimizerService } from '@sparro/core/helpers/image-optimizer';
import { UserService } from '@sparro/core/api/user/user.service';
import _ from 'lodash';
import { User } from '@sparro/app/core/models/user.model';
import { ReportDialogComponent } from '@sparro/app/shared/components/dialogs/report-dialog/report-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@sparro/env/environment';
import { SnackbarService } from '@sparro/core/helpers/toastr/snackbar.service';
import { ShareDialogComponent } from '@sparro/app/shared/components/dialogs/share-dialog/share-dialog.component';
import { UniApiConfig } from '@sparro/core/config/api.config';
import { BREAKPOINTS, UNI_CONSTANTS } from '@sparro/core/config/constants.config';
import { BreakpointObserver } from '@angular/cdk/layout';

export class LikeData {
  creation_date: string;
  user_profile_score: number;
}
@Component({
  selector: 'uni-leaderboard',
  templateUrl: './leaderboard.component.html',
  styleUrls: ['./leaderboard.component.scss'],
})
export class LeaderboardComponent implements OnInit {
  @Input() leaderboardEntries: LeaderboardEntry[] = [];
  @Input() shortlistedAnnounced: boolean = false;
  @Input() resultsAnnounced: boolean = false;
  @Input() host: string;
  @Input() chamber: number;

  sortedEntries: LeaderboardEntry[] = [];
  user: User;
  page: number = 1;
  pageOffset: number = 10;
  pagewiseEntries: PagewiseEntries[] = [];
  competitionEntryType: string;
  currentPage: number = 0;
  lastPage: number = 0;

  priorityMap = {
    "Winner": 14,
    "Runner-up": 13,
    "Best in Region": 12,
    "Honorable Mention": 11,
    "People's Choice Award": 10,
    "Best Institution for Graduation Projects": 9,
    "Institutional Excellence Award": 8,
    "Citation": 7,
    "Special Mention": 6,
    "Jury Commendation": 5,
    "Editor's Choice": 4,
    "Organizer's Choice Award": 3,
    "Shortlisted": 2,
    "Entries": 1
  };
  appealIcon: string =
    'https://uni-common-assets.s3.amazonaws.com/icons/system/svg/1/raisedhand/black_default_48x48.svg';
  pageRows: number[] = [5, 10, 15, 20];
  mobileView: boolean = false;
  likesAscending: boolean = false;
  juryScoreAscending: boolean = false;

  jurorFilterOptions: string[] = [];
  categoryFilterOptions: string[] = [];
  selectedJurorOption: string = 'Combined';
  selectedCriterionOption: string = 'Combined';

  constructor(
    public imageOptimizer: ImageOptimizerService,
    public userService: UserService,
    public dialog: MatDialog,
    public snackbarService: SnackbarService,
    public _apiConfig: UniApiConfig,
    public breakpointObserver: BreakpointObserver,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initializeBreakpoints();
    this.getAuthUser();
  }

  getAuthUser(): void {
    this.userService.userInitialized.subscribe((initialized: boolean) => {
      if (initialized) {
        this.userService.user.subscribe((response: User) => {
          this.user = response;
        });
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.leaderboardEntries.length > 0) {
      this.sortedEntries = this.leaderboardEntries;
      this.jurorFilterOptions = ['Combined', ...(_.map(this.leaderboardEntries[0].jury_score, 'juror'))]
      this.leaderboardEntries.forEach((entry) => {
        let score = [];
        entry.jury_score.forEach((jurorScore) => {
          this.categoryFilterOptions = [...this.categoryFilterOptions, ...(_.forEach(jurorScore.scores, 'criterion'))];
          score = [...score, ...(_.map(jurorScore.scores, 'score'))];
        });
        entry.filtered_score = entry.average_score = _.mean(score);
      });
      this.categoryFilterOptions = ['Combined', ...(_.map(_.uniqBy(this.categoryFilterOptions, 'criterion'), 'criterion'))];
      this.competitionEntryType = this.leaderboardEntries[0].connection_type;
      this.currentPage = 1;
      this.setPagesInfo(this.currentPage);
    }
  }

  ngAfterContentChecked(): void {
    this.changeDetectorRef.detectChanges()
  }

  initializeBreakpoints() {
    this.breakpointObserver.observe([
      BREAKPOINTS.below.sm
    ]).subscribe(result => {
      if (result.breakpoints[BREAKPOINTS.below.sm] !== this.mobileView) {
        this.mobileView = !this.mobileView;
      }
    });
  }

  setPagesInfo(currentPage) {
    this.lastPage = this.leaderboardEntries.length % this.pageOffset === 0
      ? this.leaderboardEntries.length / this.pageOffset :
      Math.floor(this.leaderboardEntries.length / this.pageOffset) + 1;
    this.displayPageEntries(currentPage);
  }

  displayPageEntries(pageNumber) {
    if (this.pagewiseEntries.length > 0) {
      let pageNotFound: boolean = true;
      this.pagewiseEntries.map((page) => {
        page.isCurrentPage = false;
        if (page.pageIndex === pageNumber) {
          page.isCurrentPage = true;
          pageNotFound = false;
          this.currentPage = page.pageIndex;
          return;
        }
      });
      if (pageNotFound) {
        this.getPageEntries(pageNumber);
      }
    } else {
      this.getPageEntries(pageNumber);
    }
  }

  getPageEntries(pageNumber): void {
    const initial = pageNumber === 1 ? pageNumber - 1 : (pageNumber - 1) * this.pageOffset;
    const final = this.sortedEntries.length >= pageNumber * this.pageOffset
      ? pageNumber * this.pageOffset - 1 : this.sortedEntries.length - 1;
    const currentPageEntries: LeaderboardEntry[] = [];

    for (let index = initial; index <= final; index++) {
      const entry = this.getEffectiveScore(this.sortedEntries[index]);
      currentPageEntries.push(entry);
    }
    this.pagewiseEntries.push({
      pageIndex: pageNumber,
      entries: currentPageEntries,
      isCurrentPage: true,
    });
    this.currentPage = pageNumber;
  }

  getEffectiveScore(entry: LeaderboardEntry): LeaderboardEntry {
    if (entry.connection_type === 'project') {
      this.mapProfileScore(entry.project.like);
      entry.effective_score = _.sumBy(entry.project.like, 'user_profile_score');
      return entry;
    }
    if (entry.connection_type === 'blog') {
      this.mapProfileScore(entry.blog.like);
      entry.effective_score = _.sumBy(entry.blog.like, 'user_profile_score');
      return entry;
    }
    if (entry.connection_type === 'publication') {
      this.mapProfileScore(entry.publication.like);
      entry.effective_score = _.sumBy(entry.publication.like, 'user_profile_score');
      return entry;
    }
  }

  mapProfileScore(likes: LikeData[]): void {
    likes.forEach((like: LikeData) => {
      like.user_profile_score = like.user_profile_score > 1
        ? like.user_profile_score : like.user_profile_score + 1;
    });
  }

  renderImage(image: string): string {
    return this.imageOptimizer.renderImage(image, 50, 50);
  }

  createReport(entry: LeaderboardEntry, appeal: boolean = false) {
    if (!this.user) {
      this.redirectToLogin();
      return;
    } else {
      const dialogWidth = window.innerWidth <= 540 ? `90vw` : `527px`;
      const dialogRef = this.dialog.open(ReportDialogComponent, {
        width: dialogWidth,
        maxWidth: dialogWidth,
        data: {
          user: this.user,
          reportTypeIds: appeal ? [5] : [],
          slug:
            entry.connection_type === 'project'
              ? entry.project.slug
              : entry.connection_type === 'blog'
                ? entry.blog.slug
                : entry.publication.slug,
          source:
            entry.connection_type === 'project'
              ? 'neo_projects'
              : entry.connection_type === 'blog'
                ? 'blogs'
                : 'publications',
          url:
            entry.connection_type === 'project'
              ? `${environment.projectUrl}${entry.project.slug}`
              : entry.connection_type === 'blog'
                ? `${environment.postUrl}${entry.blog.slug}`
                : `${environment.publicationUrl}${entry.publication.slug}`,
        },
      });
      dialogRef.afterClosed().subscribe((response: any) => {
        if (response) {
          this.snackbarService.success('Report has been initiated');
        }
      });
    }
  }

  redirectToLogin(): void {
    window.location.href = `${environment.loginUrl}authentication?service=sign-in&continue=${window.location.href}`;
  }

  openShareDialog(entry): void {
    const sourceCopy =
      entry.connection_type === 'project'
        ? this._apiConfig.copyLinkUrls.projectUrl
        : entry.connection_type === 'blog'
          ? this._apiConfig.copyLinkUrls.blogUrl
          : this._apiConfig.copyLinkUrls.publicationUrl;
    const slug =
      entry.connection_type === 'project'
        ? entry.project.slug
        : entry.connection_type === 'blog'
          ? entry.blog.slug
          : entry.publication.slug;
    const content = {
      title:
        entry.connection_type === 'project'
          ? entry.project.title
          : entry.connection_type === 'blog'
            ? entry.blog.title
            : entry.publication.title,
      subtitle:
        entry.connection_type === 'project'
          ? entry.project.subtitle
          : entry.connection_type === 'blog'
            ? entry.blog.subtitle
            : entry.publication.subtitle,
      cover_image:
        entry.connection_type === 'project'
          ? entry.project.image
          : entry.connection_type === 'blog'
            ? entry.blog.image
            : entry.publication.image,
    };
    const dialogRef = this.dialog.open(ShareDialogComponent, {
      data: {
        sourceCopy: sourceCopy,
        slug: slug,
        content: content,
      },
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  goToPage(page: string) {
    switch (page) {
      case 'first':
        this.displayPageEntries(1);
        break;
      case 'next':
        this.displayPageEntries(this.currentPage + 1);
        break;
      case 'previous':
        this.displayPageEntries(this.currentPage - 1);
        break;
      case 'last':
        this.displayPageEntries(this.lastPage);
    }
  }

  openProjectNewWindow(entry) {
    if (entry.connection_type === 'project') {
      window.open(`${environment.projectUrl}${entry.project.slug}`, '_blank');
    }
    if (entry.connection_type === 'blog') {
      window.open(`${environment.postUrl}${entry.blog.slug}`, '_blank');
    }
    if (entry.connection_type === 'publication') {
      window.open(`${environment.publicationUrl}${entry.publication.slug}`, '_blank');
    }
  }

  setFilterOption(option, optionType) {
    optionType === 'juror' ? this.applyJurorCriteriaFilter(option, '') : this.applyJurorCriteriaFilter('', option);
  }

  applyJurorCriteriaFilter(juror, criterion) {
    this.selectedJurorOption = juror ? juror : this.selectedJurorOption;
    this.selectedCriterionOption = criterion ? criterion : this.selectedCriterionOption;
    this.leaderboardEntries.forEach((entry) => {
      let score = [];
      if (this.selectedJurorOption === 'Combined') {
        entry.jury_score.forEach((jurorScore) => {
          if (this.selectedCriterionOption !== 'Combined') {
            jurorScore.scores.forEach((parameter) => {
              if (parameter.criterion === this.selectedCriterionOption) {
                score = [...score, parameter];
              }
            });
          } else {
            score = [...score, ...jurorScore.scores];
          }
        });
      } else {
        const jurorScore = _.find(entry.jury_score, (score) => score.juror === this.selectedJurorOption);
        if (this.selectedCriterionOption !== 'Combined') {
          jurorScore.scores.forEach((parameter) => {
            if (parameter.criterion === this.selectedCriterionOption) {
              score = [...score, parameter];
            }
          });
        } else {
          score = [...score, ...jurorScore.scores];
        }
      }
      entry.filtered_score = _.mean(_.map(score, 'score'));
    });
  }

  filter(dateRange) {
    this.sortedEntries.forEach((entry: LeaderboardEntry) => {
      this.applyDateRangeFilter(entry.connection_type === 'project' ? entry.project :
        entry.connection_type === 'blog' ? entry.blog : entry.publication, dateRange);
    });
  }

  applyDateRangeFilter(entry, dateRange) {
    entry.like.filter((like) => {
      if (((dateRange.startDate) <= (like.creation_date)) && ((like.creation_date) <= (dateRange.endDate))) {
        return like;
      }
    });
    this.applySorting(this.sortedEntries, 'effective_score', 'desc');
  }

  applySorting(
    entries: LeaderboardEntry[],
    orderParameter: string,
    order: string
  ) {
    this.sortedEntries = [];
    if (orderParameter === 'entryType') {
      if (order === 'desc') {
        this.sortedEntries = entries.sort(
          (a, b) =>
            this.priorityMap[b.entry_type.title] - this.priorityMap[a.entry_type.title]
        );
        this.pagewiseEntries = [];
        this.displayPageEntries(this.currentPage);
      } else {
        this.sortedEntries = entries.sort(
          (a, b) =>
            this.priorityMap[a.entry_type.title] - this.priorityMap[b.entry_type.title]
        );
        this.pagewiseEntries = [];
        this.displayPageEntries(this.currentPage);
      }
    }
    else {
      if (orderParameter === 'effective_score') {
        this.likesAscending = !this.likesAscending;
      }
      if (orderParameter === 'average_score') {
        this.juryScoreAscending = !this.juryScoreAscending;
      }
      this.sortedEntries = _.orderBy(entries, [orderParameter], [order]);
      this.pagewiseEntries = [];
      this.displayPageEntries(this.currentPage);
    }
  }

  getEntryDetail(entry: LeaderboardEntry): void {
    if (this.host === UNI_CONSTANTS.HOSTS.ARENA) {
      const url = entry.connection_type === 'project' ? `${environment.projectUrl}${entry.project.slug}` : entry.connection_type === 'blog'
        ? `${environment.postUrl}${entry.blog.slug}` : `${environment.publicationUrl}${entry.publication.slug}`;
      window.open(url, '_blank');
    }
    else {
      // const entrySlug = entry[entry.connection_type].slug;
      const url = `${this.chamber}/dashboards/competition/${entry.competition}/entry/${entry.id}`;
      window.open(url, '_self');
    }
  }

  changeRowsPerPage(rows) {
    this.pageOffset = rows;
    this.pagewiseEntries = [];
    this.setPagesInfo(this.currentPage);
  }
}
